import styled from 'styled-components';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from './Button/Button';
import { useEventContext } from '../context/EventContext';
import Typography from './Typography';
import ClipboardButton from './ClipboardButton/ClipboardButton';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from './Icons/ChevronLeftIcon';
import ArrowRightUpIcon from './Icons/ArrowRightUpIcon';
import BackIcon from './Icons/BackIcon';
import { theme } from '../theme';
import Tag from './Tag';
import { isEventPast } from '../utils/dateUtils';
import { isOrganizerAnalyticsUiEnabled } from '../config/analyticsFeatureFlags';


const EventManagementHeader = () => {
  const { eventDetails } = useEventContext();
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('eventManagementHeader');

  if (!eventDetails) {
    return <p>{t('eventNotAvailable')}</p>;
  }
  const { title, slug, status, endDate } = eventDetails;

  // Verificar si el evento ha pasado
  const eventHasPassed = isEventPast(endDate);

  // Construir la URL del evento
  const eventLink = `${process.env.REACT_APP_CLIENT_URL}/events/${id}/${slug}`;


  return (
    <>
      <HeaderWrapper>
        <Container>
          <Header>
            <HeaderActions>
              <BackButton $variant="ghost" size="l" sizeTablet="l" sizeMobile="m" onClick={() => navigate('/hosted-events')}>
                <BackIcon height={10} width={10} fill={theme.colors.defaultStrong} />
                {/* <ChevronLeftIcon fill={theme.colors.defaultStrong} /> */}
                {t('backButton')}
              </BackButton>
              <ButtonsContainer>
                <ClipboardButton
                  text={eventLink}
                  $variant="ghost"
                  aria-label={t('eventDetailHeader.shareButton')}
                  label={t('eventDetailHeader.shareButton')}
                  tooltipText={t('eventDetailHeader.copiedUrl')}
                  size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                // $iconOnly
                />
                <Button
                  $variant="ghost"
                  size="l"
                  sizeTablet="l"
                  sizeMobile="m"
                  as={Link}
                  to={`/events/${id}/${slug}`}>
                  {t('viewEventButton')}
                  <ArrowRightUpIcon fill={theme.colors.defaultStrong} />
                </Button>
              </ButtonsContainer>
            </HeaderActions>
            <HeaderTitle>
              <Typography as="h1" $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="title-5-semibold">
                {title}
              </Typography>
              {(status === 'cancelled' || eventHasPassed) && (
                <StatusTags>
                  {status === 'cancelled' && (
                    <Tag $variant="subtle">
                      {t('cancelledLabel')}
                    </Tag>
                  )}
                  {eventHasPassed && status !== 'cancelled' && (
                    <Tag $variant="subtle">
                      {t('finishedLabel')}
                    </Tag>
                  )}
                </StatusTags>
              )}
            </HeaderTitle>
          </Header>
          <SectionTabs>
            <Link
              to={`/events/manage/${id}`}
              className={`SectionTab ${location.pathname === `/events/manage/${id}` ? 'Active' : ''}`}
            >
              <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                {t('tabs.summary')}
              </Typography>
            </Link>
            <Link
              to={`/events/manage/${id}/attendees`}
              className={`SectionTab ${location.pathname.includes('attendees') ? 'Active' : ''}`}
            >
              <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                {t('tabs.attendees')}
              </Typography>
            </Link>
            <Link
              to={`/events/manage/${id}/registration`}
              className={`SectionTab ${location.pathname.includes('registration') ? 'Active' : ''}`}
            >
              <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                {t('tabs.registration')}
              </Typography>
            </Link>
            {/* <Link
              to={`/events/manage/${id}/communications`}
              className={`SectionTab ${location.pathname.includes('communications') ? 'Active' : ''}`}
            >
              <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                {t('tabs.communications')}
              </Typography>
            </Link> */}
            <Link
              to={`/events/manage/${id}/options`}
              className={`SectionTab ${location.pathname.includes('options') ? 'Active' : ''}`}
            >
              <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                {t('tabs.options')}
              </Typography>
            </Link>
                {isOrganizerAnalyticsUiEnabled ? (
                  <Link
                    to={`/events/manage/${id}/metrics`}
                    className={`SectionTab ${location.pathname.endsWith('/metrics') ? 'Active' : ''}`}
                    aria-current={location.pathname.endsWith('/metrics') ? 'page' : undefined}
                  >
                    <Typography as="span" $variant="body-1-semibold" $variantMobile="body-2-semibold">
                      {t('tabs.metrics')}
                    </Typography>
                  </Link>
                ) : null}
          </SectionTabs>
        </Container>
      </HeaderWrapper>
    </>
  );
};

export default EventManagementHeader;

const Header = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding-left: ${({ theme }) => theme.sizing.md};
  padding-right: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => theme.sizing.sm};
    padding-right: ${({ theme }) => theme.sizing.sm};
  }
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const ButtonsContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const HeaderWrapper = styled.section`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  display: flex;
  flex-direction: column; 
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  align-items: center;
  justify-content: flex-start;
`;

const StatusTags = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xxs};
  justify-content: flex-start;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} 0px;
  padding-bottom: 0;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.sm};
    padding: ${({ theme }) => theme.sizing.xs} 0px;
    padding-bottom: 0;
  }
`;

const SectionTabs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  overflow-x: auto;
  padding-left: ${({ theme }) => theme.sizing.md};

  .SectionTab {
    align-items: flex-end;
    background-color: transparent;
    border: none;
    border-bottom: ${({ theme }) => theme.sizing.xxs} solid transparent;
    color: ${({ theme }) => theme.colors.defaultWeak};
    display: flex;;
    justify-content: center;
    padding-bottom: ${({ theme }) => theme.sizing.sm};
    text-decoration: none;

    span {
      color: ${({ theme }) => theme.colors.defaultSubtle};
      transition: color 0.2s ease;

      &:hover {
        color: ${({ theme }) => theme.colors.defaultWeak};
      }
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-bottom: 12px;
    }
  }

  .SectionTab.Active {
    border-bottom: ${({ theme }) => theme.sizing.xxs} solid ${({ theme }) => theme.border.brandMain};

    span {
      color: ${({ theme }) => theme.colors.brandMain};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-bottom: 12px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: ${({ theme }) => theme.sizing.sm};
  }
`;

const BackButton = styled(Button)`
  padding: 8px 10px;
  margin-left: -10px;
  margin-right: 16px;
`;
